import {emptyMediaMixin} from "@/domain/galleries/mixins";
import {galleryTypes} from "@/domain/galleries/constants";


const flattenGalleries = galleries =>
    galleries.reduce((acc, {sub_galleries, ...mainGallery}) =>
        [...acc, mainGallery, ...sub_galleries], [])


const state = {
    galleries: {},
}

const getters = {
    galleries: state => state.galleries,
    paidGalleries: state => state.galleries && state.galleries[galleryTypes.PAID],
    unlockedGalleries: state => state.galleries && state.galleries[galleryTypes.UNLOCKED],
    favoriteGalleries: state => state.galleries && state.galleries[galleryTypes.FAVORITE],
    paidGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.PAID]) {
            return 0
        }
        return state.galleries[galleryTypes.PAID].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    unlockedGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.UNLOCKED]) {
            return 0
        }
        return state.galleries[galleryTypes.UNLOCKED].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    favoriteGalleriesMediasCount: state => {
        if (!state.galleries || !state.galleries[galleryTypes.FAVORITE]) {
            return 0
        }
        return state.galleries[galleryTypes.FAVORITE].reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    totalMediasCount: state => {
        if (!state.galleries) {
            return 0
        }
        return Object.values(state.galleries).flat().reduce((acc, gallery) => acc + gallery.media_count, 0)
    },
    priceList: (state) => ({items, galleryId}) => {
        if (!items && galleryId) {
            const gallery = Object.values(state.galleries).flat().find(gallery => gallery.id === galleryId);
            if (!gallery || !gallery.price_list || !gallery.price_list.items) return false;
            items = gallery.price_list.items;
        }
        if (!items || !items.length) return false;
        let prices = items.filter(item => item.unit_price || item.price);
        // if is range price list
        if (prices.some(item => item.from_qty && item.to_qty)) {
            prices = prices.map(item => ({
                key: `${item.from_qty}-${item.to_qty}`,
                from_qty: item.from_qty,
                to_qty: item.to_qty,
                price: item.unit_price,
                price_original: item.unit_price,
            }));
        } else {
            prices = prices.map(item => ({
                key: item.qty,
                qty: item.qty,
                price: item.price || Math.round(item.unit_price * item.qty * 10) / 10,
                price_original: item.price_original || Math.round(item.unit_price * item.qty * 10) / 10
            }));
        }
        if (prices.length > 0) prices[prices.length - 1].last = true;
        return prices;
    }
}

const mutations = {
    setGalleries(state, galleries) {
        Object.values(galleryTypes).forEach(type => {
            // Flatten sub-galleries
            const flattenedGalleries = flattenGalleries(galleries[type]);
            // Set empty medias for each gallery
            flattenedGalleries.forEach(gallery => {
                const num_placeholder = Math.min(gallery.media_count, 20)
                gallery.medias = emptyMediaMixin(num_placeholder);
            });
            galleries[type] = flattenedGalleries;
            // remove galleries with no media and no price list
            galleries[type] = galleries[type].filter(gallery => gallery.media_count > 0 || (
                gallery.price_list
                && gallery.price_list.items
                && gallery.price_list.items.length > 0)
            )
        });
        state.galleries = galleries;
    },
    updateGalleries(state, galleries) {
        Object.values(galleryTypes).forEach(type => {
            // If this gallery type doesn't exist in state yet, initialize it
            if (!state.galleries[type]) {
                state.galleries[type] = [];
            }

            // Flatten sub-galleries from the new data
            const flattenedNewGalleries = flattenGalleries(galleries[type]);

            // Create a map of existing galleries by ID for quick lookup
            const existingGalleriesMap = {};
            state.galleries[type].forEach(gallery => {
                existingGalleriesMap[gallery.id] = gallery;
            });

            // Create a new array for the updated galleries
            const updatedGalleries = [];

            // Process new galleries
            flattenedNewGalleries.forEach(newGallery => {
                // Filter out galleries with no media and no price list
                const hasMedia = newGallery.media_count > 0;
                const hasPriceList = newGallery.price_list &&
                    newGallery.price_list.items &&
                    newGallery.price_list.items.length > 0;

                if (!hasMedia && !hasPriceList) {
                    return; // Skip this gallery
                }

                if (existingGalleriesMap[newGallery.id]) {
                    // Update existing gallery while preserving its medias and other local state
                    const existingGallery = existingGalleriesMap[newGallery.id];

                    // Create updated gallery by merging new data with existing data
                    const updatedGallery = {
                        ...existingGallery,
                        ...newGallery,
                        // Preserve the existing medias to maintain UI state
                        medias: existingGallery.medias
                    };

                    // Update media count if it changed
                    if (existingGallery.media_count !== newGallery.media_count) {
                        // If media count increased, add empty placeholders for new medias
                        if (newGallery.media_count > existingGallery.media_count) {
                            const additionalPlaceholders = newGallery.media_count - existingGallery.media_count;
                            // Only add placeholders if there are no results yet or if we need to add more
                            if (!updatedGallery.medias.results || updatedGallery.medias.results.length === 0) {
                                updatedGallery.medias = emptyMediaMixin(Math.min(newGallery.media_count, 20));
                            }
                        }
                    }

                    updatedGalleries.push(updatedGallery);

                    // Remove from map to track which ones were processed
                    delete existingGalleriesMap[newGallery.id];
                } else {
                    // Add new gallery with empty medias
                    const num_placeholder = Math.min(newGallery.media_count, 20);
                    newGallery.medias = emptyMediaMixin(num_placeholder);
                    updatedGalleries.push(newGallery);
                }
            });

            // Update the state with the merged galleries
            state.galleries[type] = updatedGalleries;
        });
    },
    setMedias(state, {gallery_type, gallery_id, append = true, medias}) {
        const gallery = state.galleries[gallery_type].find(gallery => gallery.id === gallery_id)

        // remove placeholders
        if (gallery.medias.results.find(media => media.is_placeholder)) {
            gallery.medias.results = gallery.medias.results.filter(media => media.is_placeholder === false)
        }

        if (append) {
            // load more pagination
            gallery.medias.results = gallery.medias.results.concat(medias.results)
        } else {
            // numbered pagination
            gallery.medias.results = medias.results
        }

        gallery.medias.pagination = medias.pagination
    },
    updateMedias(state, {gallery_type, gallery_id, medias}) {
        const gallery = state.galleries[gallery_type].find(gallery => gallery.id === gallery_id)

        if (!gallery) return;

        // Create a map of existing media items by ID for quick lookup
        const existingMediaMap = {};
        gallery.medias.results.forEach(media => {
            existingMediaMap[media.id] = media;
        });

        // Create a new array with updated and new items
        const updatedResults = [];

        // Process new media items
        medias.results.forEach(newMedia => {
            if (existingMediaMap[newMedia.id]) {
                // Update existing item while preserving any local state
                updatedResults.push({
                    ...existingMediaMap[newMedia.id],
                    ...newMedia
                });
                // Remove from map to track which ones were processed
                delete existingMediaMap[newMedia.id];
            } else {
                // Add new item
                updatedResults.push(newMedia);
            }
        });

        // Add any remaining existing items that weren't in the new results
        // (This preserves items that might be on other pages)
        Object.values(existingMediaMap).forEach(media => {
            updatedResults.push(media);
        });

        // Sort the results to maintain the same order as the API response
        // You may need to adjust this based on your sorting requirements
        updatedResults.sort((a, b) => {
            const indexA = medias.results.findIndex(m => m.id === a.id);
            const indexB = medias.results.findIndex(m => m.id === b.id);

            // If both items are in the new results, sort by their position
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            // New items come before existing items that weren't in the new results
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            // For items not in the new results, maintain their relative order
            return 0;
        });

        // Update the gallery with the merged results
        gallery.medias.results = updatedResults;
        gallery.medias.pagination = medias.pagination;
    },
    removeFromFavoriteGallery(state, media) {
        const gallery = state.galleries[galleryTypes.FAVORITE].find(gallery =>
            gallery.medias.results.find(m => m.id === media.id)
        )
        if (gallery) {
            gallery.media_count -= 1
            gallery.medias.results = gallery.medias.results.filter(m => m.id !== media.id)
        }
    },
    addToFavoriteGallery(state, media) {
        const gallerySource = Object.values(state.galleries).flat().find(gallery => gallery.medias.results.find(m => m.id === media.id))
        if (gallerySource) {
            const gallery = state.galleries[galleryTypes.FAVORITE].find(gallery => gallery.id === gallerySource.id)
            if (gallery) {
                gallery.media_count += 1
                gallery.medias.results.unshift(media)
            } else {
                state.galleries[galleryTypes.FAVORITE].unshift({
                    id: gallerySource.id,
                    name: gallerySource.name,
                    layout: gallerySource.layout,
                    media_count: 1,
                    medias: {
                        results: [media],
                        pagination: {}
                    }
                })
            }
        }
    },
    updateMedia(state, {media, data}) {
        Object.values(galleryTypes).forEach(type => {
            const gallery = state.galleries[type].find(gallery =>
                gallery.medias.results.find(m => m.id === media.id)
            );
            if (gallery) {
                const mediaIndex = gallery.medias.results.findIndex(m => m.id === media.id);
                if (mediaIndex !== -1) {
                    gallery.medias.results[mediaIndex] = {
                        ...gallery.medias.results[mediaIndex],
                        ...data,
                    };
                    // Force reactivity
                    gallery.medias.results = [...gallery.medias.results];
                }
            }
        });
    },
    resetGalleries(state) {
        state.galleries = {}
    }
}

export const actions = (mediaService) => ({
        async fetchGalleries(context, {checkin_id, update = false}) {
            const response = await mediaService.getGalleries(checkin_id)
            if (update) {
                context.commit('updateGalleries', response)
            } else {
                context.commit('setGalleries', response)
            }

            return response
        },

        async fetchMediasByGallery(context, {gallery_type, checkin_id, gallery_id, append, params}) {
            const response = await mediaService.getMediasByGallery(checkin_id, gallery_id, params)
            context.commit('setMedias', {gallery_type, gallery_id, append, medias: response})
            return response
        },
        async updateMediasByGallery(context, {gallery_type, checkin_id, gallery_id, params}) {
            const response = await mediaService.getMediasByGallery(checkin_id, gallery_id, params)
            context.commit('updateMedias', {gallery_type, gallery_id, medias: response})
            return response
        },
        async toggleFavorite(context, media) {
            if (media.favorite) {
                mediaService.removeFavorite(media.id)
                context.commit('removeFromFavoriteGallery', media)
            } else {
                mediaService.addFavorite(media.id)
                context.commit('addToFavoriteGallery', media)
            }
            context.commit('updateMedia', {media, data: {favorite: !media.favorite}})
            return media.favorite
        },
        resetGalleries(context) {
            context.commit('resetGalleries')
        }
    }
)


export default mediaService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(mediaService)
})